import React, { useEffect, useMemo, useState } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Btn, Note, Modal, Input, Sel } from "./ui";
import { Icon } from "./Icons";
import { supabase, isSupabaseReady } from "./supabase";
import { TEMPERATURAS, ModalAtendido, ModalPerdido, ModalFechamento, ModalProjeto, criarVendaPendente, criarPedidoWix } from "./PlantaoModais";
import { ORIGENS_LEAD } from "./data";

const DIAS_SEMANA = [
  { id: 0, label: "Dom" }, { id: 1, label: "Seg" }, { id: 2, label: "Ter" },
  { id: 3, label: "Qua" }, { id: 4, label: "Qui" }, { id: 5, label: "Sex" }, { id: 6, label: "Sáb" },
];

// ═══════════════════════════════════════════════════════════════════════
// PLANTÃO — View corretor (CRM)
// Ciclo: novo → atendido (valor proposta + temperatura) → fechado (ficha) / perdido
// Adesão livre e sem penalidade (parceria comercial autônoma).
// ═══════════════════════════════════════════════════════════════════════

function formatarTelefone(t) {
  if (!t) return "";
  const s = String(t).replace(/\D/g, "");
  if (s.length >= 12) return `+${s.slice(0, 2)} ${s.slice(2, 4)} ${s.slice(4, 9)}-${s.slice(9)}`;
  return t;
}

export default function PlantaoCorretor({ currentUser }) {
  const [plantao, setPlantao] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editandoConfig, setEditandoConfig] = useState(false);
  const [modalAtendido, setModalAtendido] = useState(null); // lead
  const [modalPerdido, setModalPerdido] = useState(null);   // lead
  const [modalFechar, setModalFechar] = useState(null);     // lead
  const [modalProjeto, setModalProjeto] = useState(null);   // lead (leitura)
  const [modalProposta, setModalProposta] = useState(null); // lead (criar proposta)
  const [novoLead, setNovoLead] = useState(false);
  const [novaVenda, setNovaVenda] = useState(false);        // +Nova venda (fechamento direto)
  const [periodo, setPeriodo] = useState("mes");            // filtro dos KPIs: semana | mes | ano | tudo
  const uuidCorretor = currentUser?._corretorUuid;

  async function carregar() {
    if (!isSupabaseReady || !uuidCorretor) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: p } = await supabase
        .from("plantao_corretores")
        .select("*")
        .eq("corretor_id", uuidCorretor)
        .maybeSingle();
      setPlantao(p);
      if (p?.id) {
        const { data: l } = await supabase
          .from("plantao_leads")
          .select("*")
          .eq("plantao_corretor_id", p.id)
          .order("created_at", { ascending: false })
          .limit(80);
        setLeads(l || []);
      } else {
        setLeads([]);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); }, [uuidCorretor]);

  async function salvarAtendido(leadId, { valor_proposta, temperatura }) {
    try {
      await supabase.from("plantao_leads").update({
        status: "atendido",
        atendido_em: new Date().toISOString(),
        valor_proposta: valor_proposta === "" || valor_proposta == null ? null : parseFloat(valor_proposta),
        temperatura: temperatura || "morno",
      }).eq("id", leadId);
      setModalAtendido(null);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function salvarPerdido(leadId, { motivo }) {
    try {
      await supabase.from("plantao_leads").update({
        status: "perdido",
        motivo_perda: motivo || "Outro",
      }).eq("id", leadId);
      setModalPerdido(null);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }

  // Insere a proposta em `propostas` + marca o lead como "proposta" + repassa pro Zapier
  async function inserirProposta(leadId, ficha) {
    const valorTotal = parseFloat(ficha.financeiro?.valor_total) || 0;
    const lead = leads.find(l => l.id === leadId) || { id: leadId };
    const nome = [ficha.pedido_info?.nome, ficha.pedido_info?.sobrenome].filter(Boolean).join(" ") || lead.nome_lead || null;
    const { data: prop } = await supabase.from("propostas").insert({
      lead_id: leadId, corretor_id: uuidCorretor,
      cliente_nome: nome, pedido_info: ficha.pedido_info, moveis: ficha.moveis,
      financeiro: ficha.financeiro, template: ficha.template || null,
      valor_total: valorTotal, status: "enviada",
    }).select("id").single();
    try {
      await fetch("/api/proposta-sync", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          proposta_id: prop?.id, nome: `Proposta ${nome || ""}`.trim(),
          cliente_nome: nome, cliente_email: ficha.pedido_info?.email || "",
          template: ficha.template || "", valor_total: valorTotal, moveis: ficha.moveis || [],
        }),
      });
    } catch (_) {}
    await supabase.from("plantao_leads").update({
      status: "proposta", valor_proposta: valorTotal || null,
      pedido_info: ficha.pedido_info, moveis: ficha.moveis, financeiro: ficha.financeiro,
    }).eq("id", leadId);
  }

  async function salvarFechamento(leadId, ficha) {
    try {
      const entrega = ficha.financeiro?.entrega || "fabricar";
      // FABRICAR → gera proposta/contrato pro cliente assinar (vai pra fabricação após assinar)
      if (entrega === "fabricar") {
        await inserirProposta(leadId, ficha);
        setModalFechar(null);
        carregar();
        alert("Fechado (fabricar)! Gerei a proposta/contrato pro cliente assinar. Veja em Vendas → Propostas.");
        return;
      }
      // PRONTA ENTREGA → venda pendente → admin confirma → pedido Wix
      const valorTotal = parseFloat(ficha.financeiro?.valor_total) || 0;
      await supabase.from("plantao_leads").update({
        status: "fechado", fechado_em: new Date().toISOString(), valor_fechado: valorTotal || null,
        pedido_info: ficha.pedido_info, moveis: ficha.moveis, financeiro: ficha.financeiro,
      }).eq("id", leadId);
      const lead = leads.find(l => l.id === leadId) || { id: leadId };
      const v = await criarVendaPendente({ ...lead, id: leadId }, ficha, uuidCorretor);
      if (v?.id) await supabase.from("plantao_leads").update({ venda_id: v.id }).eq("id", leadId);
      setModalFechar(null);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }

  // Temperatura direto no card do lead
  async function setTemperaturaLead(leadId, temp) {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, temperatura: temp } : l));
    try { await supabase.from("plantao_leads").update({ temperatura: temp }).eq("id", leadId); } catch (e) { /* silencioso */ }
  }

  // Criar proposta (apresentação, antes de fechar) — grava em `propostas` + lead vira "proposta"
  async function salvarProposta(leadId, ficha) {
    try {
      await inserirProposta(leadId, ficha);
      setModalProposta(null);
      carregar();
      alert("Proposta criada! Veja em Vendas → Propostas pra criar no Wix Proposals.");
    } catch (e) { alert("Erro ao criar proposta: " + e.message); }
  }

  // +Nova venda (corretor) — fechamento direto, sem precisar de lead. Venda pendente.
  async function salvarNovaVendaCorretor(_id, ficha) {
    try {
      const lead = {
        id: null,
        nome_lead: [ficha.pedido_info?.nome, ficha.pedido_info?.sobrenome].filter(Boolean).join(" "),
        telefone_lead: ficha.pedido_info?.whatsapp || ficha.pedido_info?.telefone || "",
        campanha: "manual",
      };
      await criarVendaPendente(lead, ficha, uuidCorretor);
      setNovaVenda(false);
      alert("Venda registrada! Aguardando o gestor confirmar.");
    } catch (e) { alert("Erro ao registrar venda: " + e.message); }
  }

  // Lead manual (walk-in) do próprio corretor
  async function salvarLeadCorretor(d) {
    if (!plantao?.id) return;
    try {
      const { error } = await supabase.from("plantao_leads").insert({
        plantao_corretor_id: plantao.id,
        nome_corretor: plantao.nome || null,
        telefone_corretor: plantao.telefone || null,
        nome_lead: [d.nome_lead, d.sobrenome_lead].map(s => (s || "").trim()).filter(Boolean).join(" ").toUpperCase(),
        telefone_lead: (d.telefone_lead || "").replace(/\D/g, ""),
        campanha: d.origem || "manual",
        origem: d.origem || "manual",
        status: "redirecionado",
        lead_manual: true,
        segmento: d.segmento || null,
        created_at: new Date().toISOString(),
      });
      if (error) throw error;
      setNovoLead(false);
      carregar();
    } catch (e) { alert("Erro ao adicionar lead: " + e.message); }
  }

  // Consórcio: na quitação, aí sim cria o pedido Wix + número → vai pra fabricação
  async function marcarQuitadoLead(lead) {
    if (!window.confirm("Confirmar quitação do consórcio? Vai gerar o número do pedido no Wix e liberar pra fabricação.")) return;
    try {
      const ficha = { moveis: lead.moveis, pedido_info: lead.pedido_info, financeiro: lead.financeiro };
      const w = await criarPedidoWix(lead, ficha, uuidCorretor, lead.venda_id);
      if (w && w.ok && w.numero) {
        await supabase.from("plantao_leads").update({ wix_numero: String(w.numero), consorcio_quitado: true }).eq("id", lead.id);
        carregar();
      } else {
        alert("Falha ao criar pedido Wix: " + (w && (w.erro || JSON.stringify(w.resposta)) || "erro"));
      }
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function togglePlantao() {
    if (!plantao?.id) return;
    const novoEstado = !plantao.ativo;
    const msg = novoEstado
      ? "Voltar pro plantão? Você começa a receber leads no rodízio novamente."
      : "Sair do plantão hoje? Você para de receber leads até reativar. Sem penalidade — adesão é livre.";
    if (!window.confirm(msg)) return;
    try {
      await supabase.from("plantao_corretores").update({ ativo: novoEstado }).eq("id", plantao.id);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function salvarConfig(config) {
    try {
      const { error } = await supabase
        .from("plantao_corretores")
        .update({
          horarios: config.horarios || [],
          max_leads_dia: config.max_leads_dia === "" || config.max_leads_dia == null ? null : parseInt(config.max_leads_dia, 10),
        })
        .eq("id", plantao.id);
      if (error) throw error;
      setEditandoConfig(false);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }

  const stats = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    let inicio = null;
    if (periodo === "semana") inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - hoje.getDay());
    else if (periodo === "mes") inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    else if (periodo === "ano") inicio = new Date(hoje.getFullYear(), 0, 1);
    // "tudo" → inicio null (sem recorte)
    const noPeriodo = (d) => !inicio || (d && new Date(d) >= inicio);

    const leadsP = leads.filter(l => noPeriodo(l.created_at));
    const trabalhados = leadsP.filter(l => ["atendido", "proposta", "fechado", "perdido"].includes(l.status));
    const fechados = leadsP.filter(l => l.status === "fechado");
    const quentes = leadsP.filter(l => l.temperatura === "quente" && !["fechado", "perdido"].includes(l.status)).length;
    const taxaFech = leadsP.length > 0 ? Math.round((fechados.length / leadsP.length) * 100) : 0;
    const valorFechado = fechados.reduce((s, l) => s + (parseFloat(l.valor_fechado) || 0), 0);
    // Pipeline = leads abertos com valor de proposta (não depende do período)
    const pipeline = leads
      .filter(l => ["atendido", "proposta", "redirecionado"].includes(l.status))
      .reduce((s, l) => s + (parseFloat(l.valor_proposta) || 0), 0);
    // Leads por origem (metrificar)
    const porOrigem = {};
    ORIGENS_LEAD.forEach(o => { porOrigem[o.id] = 0; });
    leadsP.forEach(l => {
      const o = (l.origem || l.campanha || "manual").toLowerCase();
      const key = porOrigem[o] !== undefined ? o : "manual";
      porOrigem[key] = (porOrigem[key] || 0) + 1;
    });
    return {
      leadsMes: leadsP.length, atendidos: trabalhados.length, fechados: fechados.length,
      quentes, taxaFech, valorFechadoMes: valorFechado, pipeline, porOrigem,
    };
  }, [leads, periodo]);

  const periodoLabel = { semana: "Semana", mes: "Mês", ano: "Ano", tudo: "Tudo" }[periodo] || "Mês";

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Carregando…</div>;

  if (!plantao) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <SectionHeader
          title="Meus Leads · Plantão WhatsApp"
          subtitle="Rodízio de leads da campanha Bellanor no seu WhatsApp"
          icon={<Icon.rocket size={24} />}
        />
        <Note tone="info" icon={<Icon.info size={14} />}>
          Você ainda não está cadastrado no plantão. Fale com o time comercial pra entrar no rodízio de leads da campanha. Adesão é 100% voluntária.
        </Note>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader
        title="Meus Leads · Plantão WhatsApp"
        subtitle="Atenda, feche e acompanhe suas oportunidades da campanha Bellanor"
        icon={<Icon.rocket size={24} />}
        right={
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Btn onClick={() => setNovoLead(true)} variant="primary">+ Adicionar lead</Btn>
            <Btn onClick={() => setEditandoConfig(true)} variant="ghost">⚙ Meus horários</Btn>
            <Btn onClick={togglePlantao} variant={plantao.ativo ? "ghost" : "primary"}>
              {plantao.ativo ? "⏸ Sair do plantão hoje" : "▶ Voltar pro plantão"}
            </Btn>
          </div>
        }
      />

      <Note tone={plantao.ativo ? "ok" : "warn"} icon={<Icon.info size={14} />}>
        {plantao.ativo
          ? <>🟢 <b>Você está no plantão.</b> Novos leads chegam no seu WhatsApp ({formatarTelefone(plantao.telefone)}) quando for a sua vez no rodízio. Pode sair a qualquer momento — sem penalidade.</>
          : <>⏸ <b>Você está fora do plantão.</b> Nenhum lead novo vai chegar até reativar. Sem problema — participação é livre.</>}
      </Note>

      {/* Filtro de período dos KPIs */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>Período:</span>
        {[["semana", "Semana"], ["mes", "Mês"], ["ano", "Ano"], ["tudo", "Tudo"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setPeriodo(id)} style={{
            padding: "5px 12px", borderRadius: 100, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit",
            background: periodo === id ? T.brand + "22" : T.card,
            border: `1px solid ${periodo === id ? T.brand : T.border}`,
            color: periodo === id ? T.brand : T.textSec,
          }}>{lbl}</button>
        ))}
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        <StatCard label={`Leads (${periodoLabel})`} valor={stats.leadsMes} cor={T.brand} />
        <StatCard label="Atendidos" valor={stats.atendidos} cor={T.info} />
        <StatCard label="Fechados" valor={stats.fechados} cor={T.ok} />
        <StatCard label="Taxa fechamento" valor={`${stats.taxaFech}%`} cor={T.gold} />
        <StatCard label="🔥 Quentes" valor={stats.quentes} cor={T.err} />
        <StatCard label={`Fechado (${periodoLabel})`} valor={fmt.brl(stats.valorFechadoMes)} cor={T.ok} />
      </div>

      {/* Leads por origem */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: "10px 14px" }}>
        <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 8 }}>Leads por origem ({periodoLabel})</div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {ORIGENS_LEAD.map(o => (
            <div key={o.id} style={{ fontSize: 12, color: T.textSec }}>
              {o.label.split(" ")[0]}: <b style={{ color: T.text }}>{stats.porOrigem?.[o.id] || 0}</b>
            </div>
          ))}
        </div>
      </div>

      {/* Lista leads */}
      <div>
        <div style={{ fontSize: 11, color: T.textSub, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
          Meus leads · pipeline em aberto: <span style={{ color: T.gold }}>{fmt.brl(stats.pipeline)}</span>
        </div>
        {leads.length === 0 ? (
          <Card>
            <div style={{ textAlign: "center", padding: 30, color: T.textDim, fontSize: 13 }}>
              Nenhum lead ainda. Quando a sua vez chegar no rodízio, o cliente cai direto no seu WhatsApp.
            </div>
          </Card>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {leads.map(l => (
              <LeadCard
                key={l.id}
                lead={l}
                onFechar={() => setModalFechar(l)}
                onCriarProposta={() => setModalProposta(l)}
                onVerProjeto={() => setModalProjeto(l)}
                onPerdido={() => setModalPerdido(l)}
                onQuitar={() => marcarQuitadoLead(l)}
                onTemperatura={setTemperaturaLead}
              />
            ))}
          </div>
        )}
      </div>

      {editandoConfig && (
        <FormHorarios plantao={plantao} onSalvar={salvarConfig} onCancelar={() => setEditandoConfig(false)} />
      )}
      {modalAtendido && (
        <ModalAtendido lead={modalAtendido} onSalvar={salvarAtendido} onCancelar={() => setModalAtendido(null)} />
      )}
      {modalPerdido && (
        <ModalPerdido lead={modalPerdido} onSalvar={salvarPerdido} onCancelar={() => setModalPerdido(null)} />
      )}
      {modalFechar && (
        <ModalFechamento lead={modalFechar} onSalvar={salvarFechamento} onCancelar={() => setModalFechar(null)} scopeCorretorId={uuidCorretor} currentUser={currentUser} />
      )}
      {modalProposta && (
        <ModalFechamento
          lead={modalProposta}
          onSalvar={salvarProposta}
          onCancelar={() => setModalProposta(null)}
          scopeCorretorId={uuidCorretor}
          currentUser={currentUser}
          modoProposta
          modoTitulo="Nova proposta"
        />
      )}
      {modalProjeto && (
        <ModalProjeto
          lead={modalProjeto}
          onEditar={() => { const l = modalProjeto; setModalProjeto(null); setModalFechar(l); }}
          onClose={() => setModalProjeto(null)}
        />
      )}
      {novoLead && (
        <LeadFormCorretor onSalvar={salvarLeadCorretor} onCancelar={() => setNovoLead(false)} />
      )}
      {novaVenda && (
        <ModalFechamento
          lead={{ id: null, nome_lead: "", telefone_lead: "", campanha: "manual", pedido_info: {}, moveis: [], financeiro: {} }}
          onSalvar={salvarNovaVendaCorretor}
          onCancelar={() => setNovaVenda(false)}
          isAdmin={false}
          currentUser={currentUser}
          modoTitulo="Nova venda"
          scopeCorretorId={uuidCorretor}
        />
      )}
    </div>
  );
}

function LeadFormCorretor({ onSalvar, onCancelar }) {
  const [d, setD] = useState({ nome_lead: "", sobrenome_lead: "", telefone_lead: "", campanha: "manual", segmento: "", origem: "prospeccao" });
  const pode = d.nome_lead.trim();
  const labelStyle = { fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600 };
  const selStyle = { width: "100%", fontSize: 13, padding: "10px 12px", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text, boxSizing: "border-box" };
  return (
    <Modal title="Adicionar lead" onClose={onCancelar} width={480}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>Cliente que chegou direto pra você (walk-in). Entra no seu funil.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Input label="Nome" value={d.nome_lead} onChange={e => setD({ ...d, nome_lead: e.target.value.toUpperCase() })} />
        <Input label="Sobrenome" value={d.sobrenome_lead} onChange={e => setD({ ...d, sobrenome_lead: e.target.value.toUpperCase() })} />
        <Input label="WhatsApp / telefone" value={d.telefone_lead} onChange={e => setD({ ...d, telefone_lead: e.target.value })} />
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={labelStyle}>Segmento</div>
        <Sel value={d.segmento} onChange={e => setD({ ...d, segmento: e.target.value })} style={selStyle}>
          <option value="">—</option>
          <option value="salao">Salão de beleza</option>
          <option value="barbearia">Barbearia</option>
          <option value="clinica">Clínica de estética</option>
          <option value="outro">Outro</option>
        </Sel>
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={labelStyle}>Origem do lead</div>
        <Sel value={d.origem} onChange={e => setD({ ...d, origem: e.target.value })} style={selStyle}>
          {ORIGENS_LEAD.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </Sel>
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn onClick={() => pode && onSalvar(d)} disabled={!pode} icon={<Icon.check size={14} />}>Adicionar</Btn>
      </div>
    </Modal>
  );
}

function FormHorarios({ plantao, onSalvar, onCancelar }) {
  const [horarios, setHorarios] = useState(plantao.horarios || []);
  const [maxDia, setMaxDia] = useState(plantao.max_leads_dia == null ? "" : plantao.max_leads_dia);

  function toggleDia(diaId) {
    const existente = horarios.find(h => h.dia === diaId);
    if (existente) {
      setHorarios(horarios.filter(h => h.dia !== diaId));
    } else {
      const ini = diaId === 6 ? "09:00" : "08:00";
      const fim = diaId === 6 ? "13:00" : "18:00";
      setHorarios([...horarios, { dia: diaId, inicio: ini, fim: fim }]);
    }
  }

  function editarHora(diaId, campo, valor) {
    setHorarios(horarios.map(h => h.dia === diaId ? { ...h, [campo]: valor } : h));
  }

  return (
    <Modal title="Meus horários de plantão" onClose={onCancelar} width={560}>
      <div style={{ fontSize: 11, color: T.textSub, marginBottom: 12 }}>
        Escolha em quais dias e horários você quer receber leads da campanha no seu WhatsApp. Fora dessas janelas o rodízio pula seu número.
      </div>
      <div style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: 14 }}>
        {DIAS_SEMANA.map(d => {
          const h = horarios.find(x => x.dia === d.id);
          const ativo = !!h;
          return (
            <div key={d.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
              borderBottom: d.id < 6 ? `1px dashed ${T.border}` : "none",
            }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 80, cursor: "pointer" }}>
                <input type="checkbox" checked={ativo} onChange={() => toggleDia(d.id)} style={{ accentColor: T.brand, cursor: "pointer" }} />
                <span style={{ fontSize: 12, color: T.text, fontWeight: ativo ? 600 : 400 }}>{d.label}</span>
              </label>
              {ativo ? (
                <>
                  <input type="time" value={h.inicio} onChange={e => editarHora(d.id, "inicio", e.target.value)}
                    style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "4px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
                  <span style={{ color: T.textDim, fontSize: 11 }}>até</span>
                  <input type="time" value={h.fim} onChange={e => editarHora(d.id, "fim", e.target.value)}
                    style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "4px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
                </>
              ) : (
                <span style={{ color: T.textDim, fontSize: 11, fontStyle: "italic" }}>Não atende</span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12 }}>
        <Input
          label="Máximo de leads por dia (opcional, vazio = ilimitado)"
          type="number"
          value={maxDia}
          onChange={e => setMaxDia(e.target.value)}
          placeholder="Ex: 2 pra receber pouco"
        />
        <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
          Depois de bater esse limite no dia, o rodízio pula seu número até amanhã.
        </div>
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn onClick={() => onSalvar({ horarios, max_leads_dia: maxDia })} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}

function StatCard({ label, valor, cor }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${cor}`, borderRadius: T.r.md, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{valor}</div>
    </div>
  );
}

function LeadCard({ lead, onFechar, onCriarProposta, onVerProjeto, onPerdido, onQuitar, onTemperatura }) {
  const dt = new Date(lead.created_at).toLocaleString("pt-BR");
  const statusInfo = {
    redirecionado: { cor: T.brand,   label: "🟡 Aguardando você atender" },
    atendido:      { cor: T.info,    label: "📞 Em atendimento" },
    proposta:      { cor: T.gold,    label: "📄 Proposta enviada" },
    fechado:       { cor: T.ok,      label: "✅ Fechado" },
    perdido:       { cor: T.err,     label: "❌ Perdido" },
    expirado:      { cor: T.textDim, label: "⏱ Expirado" },
    fallback:      { cor: T.warn,    label: "Fallback" },
  }[lead.status] || { cor: T.textDim, label: lead.status };
  const tempInfo = TEMPERATURAS.find(t => t.id === lead.temperatura);
  const telLead = lead.telefone_lead ? String(lead.telefone_lead).replace(/\D/g, "") : "";
  const telFormatado = telLead.length === 10 || telLead.length === 11 ? "55" + telLead : telLead;
  const waHref = telLead ? `https://wa.me/${telFormatado}` : null;
  const emAberto = !["fechado", "perdido", "expirado"].includes(lead.status);

  return (
    <Card style={{ padding: 14, borderLeft: `3px solid ${statusInfo.cor}` }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {lead.status === "fechado" && lead.wix_numero && (
            <span style={{ fontSize: 11, color: T.gold, fontWeight: 700 }}>Nº {lead.wix_numero}</span>
          )}
          <span style={{ padding: "2px 8px", borderRadius: 100, background: statusInfo.cor + "22", color: statusInfo.cor, fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>{statusInfo.label}</span>
          {lead.status === "fechado" && lead.financeiro?.forma_pagamento === "consorcio" && !lead.wix_numero && (
            <span style={{ padding: "2px 8px", borderRadius: 100, background: T.brand + "22", color: T.brand, fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>📅 Consórcio</span>
          )}
          {tempInfo && emAberto && (
            <span style={{ padding: "2px 8px", borderRadius: 100, background: T.card, border: `1px solid ${T.border}`, fontSize: 10, fontWeight: 600 }}>{tempInfo.label}</span>
          )}
          {lead.campanha && (
            <span style={{ padding: "2px 8px", borderRadius: 100, background: T.gold + "22", color: T.gold, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>🎯 {lead.campanha}</span>
          )}
          <span style={{ fontSize: 10, color: T.textDim, marginLeft: "auto" }}>{dt}</span>
        </div>

        {/* Dados do cliente */}
        <div style={{ background: T.input, borderRadius: T.r.md, padding: "10px 12px", border: `1px dashed ${T.brand}44` }}>
          <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>👤 Cliente</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{lead.nome_lead || "— sem nome —"}</div>
            {waHref ? (
              <a href={waHref} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#25D366", textDecoration: "underline", fontWeight: 600 }}>💬 Abrir WhatsApp</a>
            ) : (
              <span style={{ fontSize: 12, color: T.textDim, fontStyle: "italic" }}>sem telefone informado</span>
            )}
          </div>
          {/* Valores */}
          {(lead.valor_proposta != null || lead.valor_fechado != null) && (
            <div style={{ marginTop: 6, fontSize: 11, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {lead.valor_proposta != null && <span style={{ color: T.gold }}>📄 Proposta: <b>{fmt.brl(lead.valor_proposta)}</b></span>}
              {lead.valor_fechado != null && <span style={{ color: T.ok }}>✅ Fechado: <b>{fmt.brl(lead.valor_fechado)}</b></span>}
              {lead.status === "perdido" && lead.motivo_perda && <span style={{ color: T.err }}>Motivo: {lead.motivo_perda}</span>}
            </div>
          )}
        </div>

        {/* Ações conforme o estágio */}
        {emAberto && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Temperatura direto no card */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: T.textDim, fontWeight: 600 }}>Temperatura:</span>
              {TEMPERATURAS.map(t => (
                <button key={t.id} onClick={() => onTemperatura(lead.id, t.id)} style={{
                  padding: "3px 10px", borderRadius: 100, cursor: "pointer", fontSize: 10, fontWeight: 600, fontFamily: "inherit",
                  background: lead.temperatura === t.id ? T.brand + "22" : T.card,
                  border: `1px solid ${lead.temperatura === t.id ? T.brand : T.border}`,
                  color: lead.temperatura === t.id ? T.brand : T.textSec,
                }}>{t.label}</button>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, flexWrap: "wrap" }}>
              <Btn onClick={onFechar} variant="success" style={{ fontSize: 11 }}>✅ Fechar</Btn>
              <Btn variant="ghost" onClick={onCriarProposta} style={{ fontSize: 11 }}>📄 Criar proposta</Btn>
              <Btn variant="ghost" onClick={onPerdido} style={{ fontSize: 11, color: T.err }}>❌ Perdido</Btn>
            </div>
          </div>
        )}
        {(lead.status === "fechado" || lead.status === "perdido") && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={onVerProjeto} style={{ fontSize: 11 }}>📋 Ver projeto</Btn>
          </div>
        )}
      </div>
    </Card>
  );
}
